/**
 * Pad leading zeros to a given number and return the value as a string.
 *
 * @param numberToPad the number to pad zeros to.
 * @param digitLengthToPadUntil the total number of digits to include in the string.
 *
 * @return a string with the given number including an appropriate number leading zeros.
 */
export function padZeros(numberToPad: number, digitLengthToPadUntil: number = 2): string {
  if (numberToPad === null || numberToPad === undefined || typeof numberToPad !== 'number') {
    return numberToPad as unknown as string;
  }

  let paddedValue = `${numberToPad}`;
  while (paddedValue.length < digitLengthToPadUntil) {
    paddedValue = `0${paddedValue}`;
  }

  return paddedValue;
}

/**
 * Ensures a given number is a number and returns it as a number type. If a given value is not a number,
 * the function will return <code>NaN</code>.
 *
 * @param numberToParse the number to parse, whether a string or a numerical value.
 *
 * @return the given value typed as a number or NaN if the given value is not a parsable number value.
 */
export function parseAsNumber(numberToParse: number | string | null | undefined): number {
  let numberValue: number;

  if (numberToParse === null || numberToParse === undefined) {
    numberValue = NaN;
  } else if (typeof numberToParse === 'string') {
    numberValue = Number.parseFloat(numberToParse);
  } else {
    numberValue = numberToParse;
  }

  return numberValue;
}

/**
 * Ensures a given number is formatted with some number of decimal places. This generates a string because a number
 * like '5' cannot be represented as the number '5.00' in JavaScript. If a value is not a number, the function will
 * return a value of zero with the appropriate number of decimals padded on to the end.
 *
 * @param numberToFormat the number to format.
 * @param numberOfDecimalPlaces precision to format the decimal as.
 *
 * @return a string with the given number formatted to the give precision.
 */
export function formatAsDecimal(
  numberToFormat: number | string | null | undefined,
  numberOfDecimalPlaces: number = 2
): string {
  let numberAsString: string;
  let numberValue: number = parseAsNumber(numberToFormat);

  if (isNaN(numberValue)) {
    numberValue = 0;
  }

  numberAsString = numberValue + '';

  if (numberOfDecimalPlaces > 0 && numberValue % 1 === 0) {
    // A number exactly divisible by 1 requires appending a decimal point.
    numberAsString += '.';
  }

  // Check for decimal places.
  const currentPrecision = numberAsString.length - numberAsString.substring(0, numberAsString.indexOf('.')).length - 1;

  if (currentPrecision > numberOfDecimalPlaces) {
    if (numberOfDecimalPlaces === 0) {
      numberAsString = numberAsString.substring(0, numberAsString.indexOf('.'));
    } else {
      numberAsString = numberAsString.substring(0, numberAsString.indexOf('.') + numberOfDecimalPlaces + 1);
    }
  } else {
    // Pad trailing zeros.
    for (let i = currentPrecision; i < numberOfDecimalPlaces; i++) {
      numberAsString += '0';
    }
  }

  return numberAsString;
}
